import React, { useState, useRef, useEffect } from "react";
import { Paper, Box, MenuItem, Select, Button, Typography, Checkbox, TextField } from "@mui/material";
import StageCanvas from "./StageCanvas";

const cameraOptions = [
    { label: "Top", value: "top" },
    { label: "Left", value: "left" },
    { label: "Right", value: "right" },
    { label: "Bottom", value: "bottom" },
    { label: "NW", value: "iso1" },
    { label: "NE", value: "iso2" },
    { label: "SW", value: "iso3" },
    { label: "SE", value: "iso4" },
    { label: "Reset View", value: "reset" },
];

const ThreeDView = ({
    open,
    viewer,
    realWidth,
    realHeight,
    grids,
    masks,
    columns,
    areaResults,
    colorList,
    luminaires,
    gridScaleX,
    gridScaleY,
    showAllMasks,
    minResult,
    scaleFactor,
    visualGrid,
    gridOffsetX,
    gridOffsetY,
}) => {
    const [hovered, setHovered] = useState(false);
    const [selectedView, setSelectedView] = useState("top");
    const [cameraCommand, setCameraCommand] = useState(null);
    const [savedCameraPosition, setSavedCameraPosition] = useState(null);
    const canvasRef = useRef(null);
    const [axesOpen, setAxesOpen] = useState(false);
    const [axesLength, setAxesLength] = useState(200);
    const [axesSpacing, setAxesSpacing] = useState(25);

    // console.log(viewer, "Viewer is not defined or not available");

    useEffect(() => {
        const node = canvasRef.current;
        if (node) {
            const blockRightClick = (e) => e.preventDefault();
            node.addEventListener('contextmenu', blockRightClick);
            return () => node.removeEventListener('contextmenu', blockRightClick);
        }
    }, []);


    const handleMouseDown = (e) => {
        if (e.button === 0) {
            canvasRef.current.style.cursor = "grabbing";
        } else if (e.button === 2) {
            canvasRef.current.style.cursor = "move";
        }
    };

    const handleMouseUp = () => {
        canvasRef.current.style.cursor = "grab";
    };

    // console.log(grids[0], "3D grids");

    return (
        open &&
        <div
            style={{
                position: "fixed",
                top: "65px",
                right: "350px",
                width: hovered ? "70vw" : "360px",
                height: hovered ? "80vh" : "360px",
                transition: "all 0.4s ease-in-out",
                zIndex: 1100,
                pointerEvents: "auto",
            }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <Paper
                elevation={hovered ? 8 : 3}
                sx={{
                    width: "100%",
                    height: "100%",
                    overflow: "hidden",
                    padding: 1,
                    borderRadius: 2,
                    display: "flex",
                    flexDirection: "column",
                    bgcolor: "#f7f7f7",
                }}
            >

                {hovered && (
                    <Box
                        sx={{
                            position: "absolute",
                            bottom: 8,
                            right: 8,
                            zIndex: 10,
                            fontSize: "12px",
                            backgroundColor: "rgba(0,0,0,0.6)",
                            color: "#fff",
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-end",
                        }}
                    >
                        <div>🖱️ Left Click: Orbit</div>
                        <div>🖱️ Right Click: Pan</div>
                        <div>🖱️ Scroll: Zoom</div>
                    </Box>
                )}


                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 3,
                        mb: 2,
                        flexWrap: "wrap", // so it still looks good on small screens
                    }}
                >
                    {/* --- VIEW CONTROLS --- */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Typography variant="body2">View:</Typography>
                        <Select
                            size="small"
                            value={selectedView}
                            onChange={(e) => setSelectedView(e.target.value)}
                            sx={{ minWidth: 140 }}
                        >
                            {cameraOptions.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Select>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setCameraCommand(selectedView)}
                        >
                            Apply
                        </Button>
                    </Box>

                    {/* --- DIVIDER (subtle vertical line) --- */}
                    <Box sx={{ width: "1px", height: 32, bgcolor: "divider" }} />

                    {/* --- AXES CONTROLS --- */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Typography variant="body2">Axes:</Typography>
                        <Checkbox
                            size="small"
                            checked={axesOpen}
                            onChange={(e) => setAxesOpen(e.target.checked)}
                        />
                        <TextField
                            size="small"
                            onChange={(e) => setAxesLength(parseInt(e.target.value))}
                            type="number"
                            value={axesLength}
                            label="Length"
                            sx={{ width: 100 }}
                        />
                        <TextField
                            size="small"
                            onChange={(e) => setAxesSpacing(parseInt(e.target.value))}
                            type="number"
                            value={axesSpacing}
                            label="Spacing"
                            sx={{ width: 100 }}
                        />
                    </Box>
                </Box>



                <Box sx={{ flexGrow: 1 }}>
                    <Box
                        sx={{ outline: "1px solid #ccc", borderRadius: 1, width: "100%", height: hovered ? "100%" : "295px", bgcolor: "#fff" }}
                        ref={canvasRef}
                        onMouseDown={handleMouseDown}
                        onMouseUp={handleMouseUp}
                    >
                        <StageCanvas
                            open={open}
                            viewer={viewer}
                            realWidth={realWidth}
                            realHeight={realHeight}
                            savedCameraPosition={savedCameraPosition}
                            setSavedCameraPosition={setSavedCameraPosition}
                            grids={grids}
                            masks={masks}
                            columns={columns}
                            cameraCommand={cameraCommand}
                            setCameraCommand={setCameraCommand}
                            areaResults={areaResults}
                            colorList={colorList}
                            luminaires={luminaires}
                            gridScaleX={gridScaleX}
                            gridScaleY={gridScaleY}
                            showAllMasks={showAllMasks}
                            minResult={minResult}
                            scaleFactor={scaleFactor}
                            visualGrid={visualGrid}
                            gridOffsetX={gridOffsetX}
                            gridOffsetY={gridOffsetY}
                            axesOpen={axesOpen}
                            axesLength={axesLength}
                            axesSpacing={axesSpacing}
                        />
                    </Box>
                </Box>
            </Paper>
        </div>
    );
};

export default ThreeDView;
